// Pure condition evaluation logic — no React/UI dependencies
import {
  calculateCartQuantity,
  calculateCartTotal,
  exceedsMaxPrice,
  exceedsMaxQuantity,
  meetsMinQuantity,
  type CartItem,
} from "@/lib/queries/products";

/** Narrow input interface for product condition matching (no UI dependency). */
export interface ProductMatchInput {
  id: number;
  category?: string | null;
  productType?: string | null;
  vendor?: string | null;
  tags?: string[] | null;
  collectionIds?: number[];
  collectionNames?: string[];
  /**
   * Underlying collection.externalId values (Shopify GID or CSV slug).
   * EPD-8597 — canonical match key against condition.collections[].id.
   * Nullable to match CartItem/MatchableProduct, since the tRPC payload returns
   * `null` for legacy collections without an externalId.
   */
  collectionExternalIds?: string[] | null;
  priceMin: number;
  priceMax: number;
}

/** Common product fields used by both cart-item and product-card matching. */
interface MatchableProduct {
  category?: string | null;
  productType?: string | null;
  vendor?: string | null;
  tags?: string[] | null;
  collectionIds?: number[] | null;
  collectionNames?: string[] | null;
  /**
   * Underlying collection.externalId values (Shopify GID or CSV slug).
   * EPD-8597 — canonical match key against condition.collections[].id.
   */
  collectionExternalIds?: string[] | null;
}

export interface SelectionRuleCondition {
  id?: string | number | null;
  conditionName: string;
  isRequired: boolean;
  quantityType: string;
  quantity: number;
  conditionOperation: string;
  vendors?: string[] | null;
  productTypes?: string[] | null;
  priceMin?: number | null;
  priceMax?: number | null;
  tags?: string[] | null;
  categories?: string[] | null;
  collections?: Array<{ id: string | number; name: string }> | null;
  options?: Array<{ name: string; values: string[] }> | null;
  products?: Array<{ id: number; name: string }> | null;
}

export interface SelectionRule {
  minQuantity?: number | null;
  maxQuantity?: number | null;
  maxPrice?: number | null;
  conditions?: SelectionRuleCondition[] | null;
}

export interface ProductConditionStatus {
  condition: SelectionRuleCondition;
  matchingQuantity: number;
  fulfilled: boolean;
  triggered: boolean;
}

export function conditionsUseProductMetadata(
  conditions: SelectionRuleCondition[],
): boolean {
  return conditions.some(
    (c) =>
      (c.categories && c.categories.length > 0) ||
      (c.productTypes && c.productTypes.length > 0) ||
      (c.vendors && c.vendors.length > 0) ||
      (c.tags && c.tags.length > 0) ||
      (c.collections && c.collections.length > 0) ||
      (c.products && c.products.length > 0),
  );
}

/**
 * Evaluate product condition statuses using AND/OR semantics.
 * Required conditions must always pass. Non-required conditions are only
 * enforced when triggered (i.e., the creator has matching items in cart).
 */
export function evaluateConditionStatuses(
  statuses: ProductConditionStatus[],
  conditions: SelectionRuleCondition[],
): boolean {
  // Required conditions must always be met
  for (const s of statuses) {
    if (s.condition.isRequired && !s.fulfilled) return false;
  }

  // Non-required: only evaluate triggered conditions
  const operation = conditions[0]?.conditionOperation?.toUpperCase() ?? "AND";
  const nonRequiredTriggered = statuses.filter(
    (s) => !s.condition.isRequired && s.triggered,
  );
  if (nonRequiredTriggered.length > 0) {
    if (operation === "OR") {
      if (!nonRequiredTriggered.some((s) => s.fulfilled)) return false;
    } else {
      if (!nonRequiredTriggered.every((s) => s.fulfilled)) return false;
    }
  }

  return true;
}

export function computeOrderConditionStatus(
  cart: CartItem[],
  selectionRule: SelectionRule,
): { fulfilled: boolean; details: string[] } {
  const details: string[] = [];
  let allMet = true;

  const qty = calculateCartQuantity(cart);

  if (selectionRule.minQuantity != null) {
    const met = meetsMinQuantity(cart, selectionRule.minQuantity);
    if (!met) allMet = false;
    details.push(
      met
        ? `Minimum ${selectionRule.minQuantity} items - met (${qty} selected)`
        : `Minimum ${selectionRule.minQuantity} items - not met (${qty} selected)`,
    );
  }

  if (selectionRule.maxQuantity != null) {
    const exceeded = exceedsMaxQuantity(cart, selectionRule.maxQuantity);
    if (exceeded) allMet = false;
    details.push(
      exceeded
        ? `Maximum ${selectionRule.maxQuantity} items - exceeded (${qty} selected)`
        : `Maximum ${selectionRule.maxQuantity} items - ${qty} selected`,
    );
  }

  if (selectionRule.maxPrice != null) {
    const exceeded = exceedsMaxPrice(cart, selectionRule.maxPrice);
    if (exceeded) allMet = false;
    const total = calculateCartTotal(cart);
    details.push(
      exceeded
        ? `Maximum total ${selectionRule.maxPrice} - exceeded (${total.toFixed(2)} in cart)`
        : `Maximum total ${selectionRule.maxPrice} - ${total.toFixed(2)} in cart`,
    );
  }

  // Check product conditions with AND/OR semantics
  const conditions = selectionRule.conditions ?? [];
  if (conditions.length > 0) {
    const conditionStatuses = computeProductConditionStatuses(cart, conditions);
    if (!evaluateConditionStatuses(conditionStatuses, conditions)) {
      allMet = false;
    }
  }

  // Empty cart cannot satisfy selection rules, so fulfilled is false
  return { fulfilled: allMet && cart.length > 0, details };
}

/**
 * Shared matching logic for categories, productTypes, vendors, tags, and
 * collections. Returns checks array entries that both cart-item and
 * product-card matchers use, avoiding duplicate code.
 */
function matchesCommonProductCriteria(
  product: MatchableProduct,
  condition: SelectionRuleCondition,
): boolean[] {
  const checks: boolean[] = [];

  if (condition.categories && condition.categories.length > 0) {
    checks.push(
      product.category != null &&
        condition.categories.some(
          (c) => c.toLowerCase() === product.category?.toLowerCase(),
        ),
    );
  }

  if (condition.productTypes && condition.productTypes.length > 0) {
    checks.push(
      product.productType != null &&
        condition.productTypes.some(
          (pt) => pt.toLowerCase() === product.productType?.toLowerCase(),
        ),
    );
  }

  if (condition.vendors && condition.vendors.length > 0) {
    checks.push(
      product.vendor != null &&
        condition.vendors.some(
          (v) => v.toLowerCase() === product.vendor?.toLowerCase(),
        ),
    );
  }

  if (condition.tags && condition.tags.length > 0) {
    // Require ALL listed tags (AND). conditionOperation is declared as "AND" by
    // the backend; using .some() here would flip it to OR and let a single-tag
    // item satisfy a multi-tag condition.
    checks.push(
      product.tags != null &&
        condition.tags.every((tag: string) =>
          product.tags?.some(
            (t: string) => t.toLowerCase() === tag.toLowerCase(),
          ),
        ),
    );
  }

  // Match by collection in three layers (any one succeeds):
  //   1. externalIdMatch — compares against the underlying collection.externalId
  //      (Shopify GID or CSV slug). This is the canonical match key:
  //      condition.collections[].id stores the same value (EPD-8597).
  //   2. idMatch — legacy compare against catalog_collection.id (join-table PK).
  //      Different domain from condition.collections[].id, so it only succeeds
  //      for the rare legacy case where the condition's id happens to be the
  //      catalog_collection.id as a string. Kept for defensive parity.
  //   3. nameMatch — fallback comparing the cart-item's collectionNames
  //      (catalog label, EPD-7108) against the condition's collection name.
  //      Works when the brand didn't relabel the collection.
  if (condition.collections && condition.collections.length > 0) {
    checks.push(
      condition.collections.some((col) => {
        const externalIdMatch =
          product.collectionExternalIds?.some(
            (ext: string) => ext === String(col.id),
          ) ?? false;
        const idMatch =
          product.collectionIds?.some(
            (id: number) => String(id) === String(col.id),
          ) ?? false;
        const nameMatch =
          product.collectionNames?.some(
            (cn: string) => cn.toLowerCase() === col.name.toLowerCase(),
          ) ?? false;
        return externalIdMatch || idMatch || nameMatch;
      }),
    );
  }

  return checks;
}

/**
 * Check if a cart item matches a condition's product criteria.
 * A cart item matches if it satisfies ALL specified criteria on the condition.
 */
export function cartItemMatchesCondition(
  item: CartItem,
  condition: SelectionRuleCondition,
): boolean {
  const checks = matchesCommonProductCriteria(item, condition);

  if (condition.products && condition.products.length > 0) {
    checks.push(
      item.productId != null &&
        condition.products.some((p) => p.id === item.productId),
    );
  }

  if (condition.priceMin != null) {
    checks.push(parseFloat(item.price) >= condition.priceMin);
  }

  if (condition.priceMax != null) {
    checks.push(parseFloat(item.price) <= condition.priceMax);
  }

  // Match variant options (e.g., Color: Black). Each condition option must
  // have a matching option on the cart item with an allowed value.
  if (condition.options && condition.options.length > 0) {
    checks.push(
      item.options != null &&
        item.options.length > 0 &&
        condition.options.every((opt) =>
          item.options?.some(
            (io) =>
              io.name.toLowerCase() === opt.name.toLowerCase() &&
              opt.values.some(
                (v) => v.toLowerCase() === io.value.toLowerCase(),
              ),
          ),
        ),
    );
  }

  // No product criteria means the condition applies to all products
  if (checks.length === 0) return true;

  return checks.every(Boolean);
}

// Evaluate whether the matching quantity satisfies a condition's quantity constraint.
// MINIMUM requires >=, MAXIMUM requires <=, EXACT requires ===.
export function checkQuantityFulfilled(
  matchingQuantity: number,
  requiredQuantity: number,
  quantityType: string,
): boolean {
  const normalized = quantityType.trim().toUpperCase();
  switch (normalized) {
    case "MINIMUM":
      return matchingQuantity >= requiredQuantity;
    case "EXACT":
      return matchingQuantity === requiredQuantity;
    default:
      // MAXIMUM: only fail when the limit is exceeded.
      // Zero matching items is valid for MAXIMUM conditions (within the limit).
      // This matches the PFA backend logic (product-fulfillment/services/src/services/utils/catalogSelectionRule.ts:290-295)
      // which has been in production and treats MAXIMUM as an upper bound constraint, not a requirement.
      return matchingQuantity <= requiredQuantity;
  }
}

/**
 * Compute the fulfillment status of each product condition against the cart.
 *
 * Required conditions: must always be satisfied.
 * Optional conditions: only enforced when the creator has matching items in cart.
 *
 * A cart item is assigned to at most one condition — required conditions first,
 * then optional in declared order. This matches the backend validator
 * (product-fulfillment/services/src/services/utils/catalogSelectionRule.ts:321-367)
 * which consumes matched items before evaluating subsequent conditions. Counting
 * each item independently per condition would double-count combo-tagged products
 * (e.g. an item tagged with both `Can` and `Sticks` against two separate
 * tag-based conditions), causing the portal to block orders the backend accepts.
 */
export function computeProductConditionStatuses(
  cart: CartItem[],
  conditions: SelectionRuleCondition[],
): ProductConditionStatus[] {
  // Evaluate required conditions first (matches backend sort on line 333 of
  // catalogSelectionRule.ts) so their claim on a combo item wins.
  const indices = conditions.map((_, i) => i);
  indices.sort((a, b) => {
    const reqA = conditions[a].isRequired ? 0 : 1;
    const reqB = conditions[b].isRequired ? 0 : 1;
    return reqA - reqB;
  });

  const available = new Set(cart.map((_, i) => i));
  const statuses: ProductConditionStatus[] = new Array(conditions.length);

  for (const condIdx of indices) {
    const condition = conditions[condIdx];
    let matchingQuantity = 0;
    const consumed: number[] = [];
    for (const itemIdx of available) {
      if (cartItemMatchesCondition(cart[itemIdx], condition)) {
        matchingQuantity += cart[itemIdx].quantity;
        consumed.push(itemIdx);
      }
    }
    for (const itemIdx of consumed) available.delete(itemIdx);

    const triggered = matchingQuantity > 0;
    const withinLimit = checkQuantityFulfilled(
      matchingQuantity,
      condition.quantity,
      condition.quantityType,
    );
    const fulfilled = condition.isRequired
      ? withinLimit
      : !triggered
        ? true
        : withinLimit;

    statuses[condIdx] = { condition, matchingQuantity, fulfilled, triggered };
  }

  return statuses;
}

/**
 * Check whether all product conditions are met, respecting AND/OR semantics.
 * Required conditions are always enforced. For non-required conditions,
 * conditionOperation determines whether ALL (AND) or ANY (OR) must be fulfilled.
 */
export function areProductConditionsMet(
  cart: CartItem[],
  conditions: SelectionRuleCondition[],
): boolean {
  if (conditions.length === 0) return true;

  const statuses = computeProductConditionStatuses(cart, conditions);
  return evaluateConditionStatuses(statuses, conditions);
}

/**
 * Check if a product matches a condition's criteria.
 * Uses overlap logic for price ranges: a product qualifies if any variant
 * could fall within the condition's price range.
 */
export function productMatchesCondition(
  product: ProductMatchInput,
  condition: SelectionRuleCondition,
): boolean {
  const checks = matchesCommonProductCriteria(product, condition);

  if (condition.products && condition.products.length > 0) {
    checks.push(condition.products.some((p) => p.id === product.id));
  }

  // Price overlap: product qualifies if its range overlaps the condition range
  if (condition.priceMin != null && condition.priceMax != null) {
    checks.push(
      product.priceMax >= condition.priceMin &&
        product.priceMin <= condition.priceMax,
    );
  } else if (condition.priceMin != null) {
    checks.push(product.priceMax >= condition.priceMin);
  } else if (condition.priceMax != null) {
    checks.push(product.priceMin <= condition.priceMax);
  }

  // No criteria means the condition applies to all products
  if (checks.length === 0) return true;

  return checks.every(Boolean);
}

/**
 * Returns which conditions a product satisfies.
 */
export function getMatchingConditions(
  product: ProductMatchInput,
  conditions: SelectionRuleCondition[],
): SelectionRuleCondition[] {
  return conditions.filter((c) => productMatchesCondition(product, c));
}

/**
 * Extract all condition-relevant values from conditions for badge highlighting.
 */
export interface ConditionCriteria {
  categories: Set<string>;
  vendors: Set<string>;
  tags: Set<string>;
  productTypes: Set<string>;
  collectionNames: Set<string>;
}

export function extractConditionCriteria(
  conditions: SelectionRuleCondition[],
): ConditionCriteria {
  const criteria: ConditionCriteria = {
    categories: new Set(),
    vendors: new Set(),
    tags: new Set(),
    productTypes: new Set(),
    collectionNames: new Set(),
  };

  for (const c of conditions) {
    if (c.categories) {
      for (const cat of c.categories)
        criteria.categories.add(cat.toLowerCase());
    }
    if (c.vendors) {
      for (const v of c.vendors) criteria.vendors.add(v.toLowerCase());
    }
    if (c.tags) {
      for (const t of c.tags) criteria.tags.add(t.toLowerCase());
    }
    if (c.productTypes) {
      for (const pt of c.productTypes)
        criteria.productTypes.add(pt.toLowerCase());
    }
    if (c.collections) {
      for (const col of c.collections)
        criteria.collectionNames.add(col.name.toLowerCase());
    }
  }

  return criteria;
}

export interface ConditionBadge {
  label: string;
  prefix?: string;
  matches: boolean;
}

/**
 * Build badge data for a product's metadata against condition criteria.
 * Shared by ProductCard (grid) and ProductDetailView (detail).
 */
export function buildConditionBadges(
  product: {
    vendor?: string | null;
    productType?: string | null;
    tags?: string[] | null;
    collectionNames?: string[];
    category?: string | null;
  },
  criteria: ConditionCriteria,
): ConditionBadge[] {
  const badges: ConditionBadge[] = [];

  if (product.vendor) {
    badges.push({
      prefix: "Vendor",
      label: product.vendor,
      matches: criteria.vendors.has(product.vendor.toLowerCase()),
    });
  }
  if (product.productType) {
    badges.push({
      prefix: "Type",
      label: product.productType,
      matches: criteria.productTypes.has(product.productType.toLowerCase()),
    });
  }
  if (product.collectionNames) {
    for (const name of product.collectionNames) {
      badges.push({
        label: name,
        matches: criteria.collectionNames.has(name.toLowerCase()),
      });
    }
  }
  if (product.category) {
    badges.push({
      label: product.category,
      matches: criteria.categories.has(product.category.toLowerCase()),
    });
  }
  if (product.tags) {
    for (const tag of product.tags) {
      badges.push({
        label: tag,
        matches: criteria.tags.has(tag.toLowerCase()),
      });
    }
  }

  return badges;
}
